import { useState, useCallback, useMemo } from 'react';
import { buildAdjacencyMap } from './graphUtils';
import type { GraphLink, NodeId } from '../../types/graph';

interface HoveredNode {
  id: NodeId;
  [key: string]: any;
}

/**
 * Hook for hover highlight interaction (Obsidian-style).
 *
 * On node hover: hovered node + direct neighbors at full opacity,
 * everything else dimmed to ~15-20% opacity.
 */
export function useGraphInteraction(containerRef: React.RefObject<any>, links: GraphLink[]) {
  const [hoveredNode, setHoveredNode] = useState<HoveredNode | null>(null);
  const [hoveredLink, setHoveredLink] = useState<GraphLink | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Pre-compute adjacency map
  const adjacencyMap = useMemo(() => buildAdjacencyMap(links || []), [links]);

  // Set of node IDs that should be highlighted
  const highlightNodes = useMemo(() => {
    if (!hoveredNode) return new Set<NodeId>();
    const neighbors = adjacencyMap.get(hoveredNode.id) || new Set<NodeId>();
    const result = new Set<NodeId>([hoveredNode.id]);
    neighbors.forEach(n => result.add(n));
    return result;
  }, [hoveredNode, adjacencyMap]);

  // Set of link keys that should be highlighted
  const highlightLinkKeys = useMemo(() => {
    if (!hoveredNode) return new Set<string>();
    const keys = new Set<string>();
    for (const link of (links || [])) {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      if (sourceId === hoveredNode.id || targetId === hoveredNode.id) {
        keys.add(`${sourceId}-${targetId}`);
      }
    }
    return keys;
  }, [hoveredNode, links]);

  const setCursorStyle = useCallback((cursor: string) => {
    const el = containerRef.current?.querySelector('canvas') as HTMLCanvasElement | null;
    if (el) el.style.cursor = cursor;
  }, [containerRef]);

  const handleNodeHover = useCallback((node: HoveredNode | null) => {
    setHoveredNode(node || null);
    setCursorStyle(node ? 'pointer' : 'default');
  }, [setCursorStyle]);

  const handleLinkHover = useCallback((link: GraphLink | null) => {
    setHoveredLink(link || null);
    setCursorStyle(link ? 'pointer' : 'default');
  }, [setCursorStyle]);

  const handleMouseMove = useCallback((event: React.MouseEvent | MouseEvent) => {
    setTooltipPos({ x: event.clientX + 15, y: event.clientY + 15 });
  }, []);

  const isNodeHighlighted = useCallback((nodeId: NodeId): boolean => {
    if (!hoveredNode) return true; // No hover = everything visible
    return highlightNodes.has(nodeId);
  }, [hoveredNode, highlightNodes]);

  const isLinkHighlighted = useCallback((link: GraphLink): boolean => {
    if (!hoveredNode) return true;
    const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
    const targetId = typeof link.target === 'object' ? link.target.id : link.target;
    return highlightLinkKeys.has(`${sourceId}-${targetId}`);
  }, [hoveredNode, highlightLinkKeys]);

  return {
    hoveredNode,
    hoveredLink,
    tooltipPos,
    adjacencyMap,
    highlightNodes,
    isNodeHighlighted,
    isLinkHighlighted,
    handleNodeHover,
    handleLinkHover,
    handleMouseMove,
    hasHover: !!hoveredNode,
  };
}
